import { v4 as uuidv4 } from 'uuid';

import { createB3TraceContext, type B3TraceContext } from './b3-trace-context.ts';
import type { AiFieldRunSource } from './types.ts';
import { useAnalytics as getAnalyticsClient } from './useAnalytics.ts';
import { getDatabaseContainerType } from './utils.ts';

const maximumDwellTimeMs = 3 * 60 * 1000;

type ActiveResultView = {
  contentId: string;
  source: AiFieldRunSource;
  surfaceId: string;
  singleInstrumentationID: string;
  traceId: string;
  viewedAt: number;
  timeoutId: ReturnType<typeof globalThis.setTimeout>;
};

const activeResultViews = new Set<ActiveResultView>();
let listeningForSurfaceExit = false;

const sendResultViewed = (
  contentId: string,
  source: AiFieldRunSource,
  singleInstrumentationID: string,
  traceId: string,
  aiDwellTimeMilliSeconds?: number
) => {
  void getAnalyticsClient().sendAiSummaryResultViewedEvent({
    contentId,
    databaseContainer: getDatabaseContainerType(),
    source,
    singleInstrumentationID,
    traceId,
    aiDwellTimeMilliSeconds
  });
};

const stopListeningForSurfaceExit = () => {
  if (activeResultViews.size > 0) {
    return;
  }

  globalThis.window.removeEventListener('pagehide', handlePageHide);
  globalThis.document.removeEventListener('visibilitychange', handleVisibilityChange);
  listeningForSurfaceExit = false;
};

const endResultView = (resultView: ActiveResultView) => {
  if (!activeResultViews.delete(resultView)) {
    return;
  }

  globalThis.clearTimeout(resultView.timeoutId);
  sendResultViewed(
    resultView.contentId,
    resultView.source,
    resultView.singleInstrumentationID,
    resultView.traceId,
    Math.min(Date.now() - resultView.viewedAt, maximumDwellTimeMs)
  );
  stopListeningForSurfaceExit();
};

function endAllResultViews() {
  for (const resultView of [...activeResultViews]) {
    endResultView(resultView);
  }
}

function handlePageHide() {
  endAllResultViews();
}

function handleVisibilityChange() {
  if (globalThis.document.visibilityState === 'hidden') {
    endAllResultViews();
  }
}

const listenForSurfaceExit = () => {
  if (listeningForSurfaceExit) {
    return;
  }

  globalThis.window.addEventListener('pagehide', handlePageHide);
  globalThis.document.addEventListener('visibilitychange', handleVisibilityChange);
  listeningForSurfaceExit = true;
};

export type AiSummaryInteraction = {
  traceContext: B3TraceContext;
  resultViewed: () => void;
  endResultView: () => void;
};

export const beginAiSummaryInteraction = (
  contentId: string,
  surfaceId: string,
  source: AiFieldRunSource
): AiSummaryInteraction => {
  const singleInstrumentationID = uuidv4();
  const traceContext = createB3TraceContext();
  let ended = false;
  let resultView: ActiveResultView | undefined;

  void getAnalyticsClient().sendAiSummaryInteractionInitiatedEvent({
    contentId,
    databaseContainer: getDatabaseContainerType(),
    source,
    singleInstrumentationID,
    traceId: traceContext.traceId
  });

  return {
    traceContext,
    resultViewed: () => {
      if (ended || resultView !== undefined) {
        return;
      }

      for (const activeResultView of [...activeResultViews]) {
        if (activeResultView.surfaceId === surfaceId) {
          endResultView(activeResultView);
        }
      }

      sendResultViewed(contentId, source, singleInstrumentationID, traceContext.traceId);
      resultView = {
        contentId,
        source,
        surfaceId,
        singleInstrumentationID,
        traceId: traceContext.traceId,
        viewedAt: Date.now(),
        timeoutId: globalThis.setTimeout(() => {
          if (resultView) {
            endResultView(resultView);
          }
        }, maximumDwellTimeMs)
      };
      activeResultViews.add(resultView);
      listenForSurfaceExit();
    },
    endResultView: () => {
      ended = true;
      if (resultView) {
        endResultView(resultView);
      }
    }
  };
};
